import { EdgeLabelRenderer, getBezierPath, getSmoothStepPath, type EdgeProps } from '@xyflow/react';
import { statusIntent, type StatusIntent } from '@/theme/tokens';
import type { OlEdge as OlEdgeType } from '@/flow-ir/toReactFlow';
import { StatusPill } from '@/components/StatusPill';
import { arrowMarker } from './arrow';

const INTENT_VAR: Record<StatusIntent, string> = {
  positive: 'var(--ol-positive)',
  attention: 'var(--ol-attention)',
  negative: 'var(--ol-negative)',
  info: 'var(--ol-info)',
  neutral: 'var(--ol-text-tertiary)',
};

/**
 * The three edge kinds, drawn distinctly (workflow-designer.md §4.5):
 *  - sequence : solid neutral, arrow — control flow, the default spine.
 *  - status   : solid, status-intent colour, arrow, + a status pill — conditional control flow.
 *  - data     : dashed, muted, NO arrowhead, + a binding chip — the data layer NEVER gates.
 */
export function OlEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps<OlEdgeType>) {
  const kind = data?.kind ?? 'sequence';
  const isData = kind === 'data';

  const [path, labelX, labelY] = isData
    ? getBezierPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, curvature: 0.4 })
    : getSmoothStepPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, borderRadius: 10 });

  let stroke = 'var(--ol-text-tertiary)';
  let markerEnd: string | undefined = arrowMarker('seq');
  let dash: string | undefined;
  let opacity = 1;
  let width = selected ? 2.6 : 1.75;

  if (kind === 'status') {
    const intent = statusIntent(data?.onStatus ?? '');
    stroke = INTENT_VAR[intent];
    markerEnd = arrowMarker(intent);
    width = selected ? 2.8 : 2;
  } else if (isData) {
    stroke = 'var(--ol-info)';
    markerEnd = undefined; // data is a side-channel, never a control arrow
    dash = '5 4';
    opacity = selected ? 0.9 : 0.5;
    width = 1.5;
  }

  // Diff highlight (spec §9.3) then diagnostic emphasis — overlays win over the base kind colour.
  if (data?.diffState === 'added') {
    stroke = 'var(--ol-positive)';
    width = Math.max(width, 2.4);
    if (!isData) markerEnd = arrowMarker('positive');
  } else if (data?.diffState === 'changed') {
    stroke = 'var(--ol-attention)';
    width = Math.max(width, 2.4);
    if (!isData) markerEnd = arrowMarker('attention');
  }
  if (data?.diag === 'error') {
    stroke = 'var(--ol-negative)';
    width = Math.max(width, 2.4);
    if (!isData) markerEnd = arrowMarker('negative');
  } else if (data?.diag === 'warning' && !data?.diffState) {
    stroke = 'var(--ol-attention)';
    width = Math.max(width, 2.2);
    if (!isData) markerEnd = arrowMarker('attention');
  }

  // On-edge diagnostic badge (inline validation): shape + count, hover reveals code + text.
  const diagCount = (data?.diagCount as number | undefined) ?? 1;
  const diagTitle = data?.diagTitle as string | undefined;
  const diagStale = Boolean(data?.diagStale);
  const diagSymbol = data?.diag === 'error' ? '●' : data?.diag === 'warning' ? '▲' : 'ℹ';

  return (
    <>
      <path
        id={id}
        d={path}
        fill="none"
        className="react-flow__edge-path"
        markerEnd={markerEnd}
        style={{ stroke, strokeWidth: width, strokeDasharray: dash, opacity }}
      />
      {/* Invisible fat hit-area for easier selection (keyboard + pointer). */}
      <path d={path} fill="none" stroke="transparent" strokeWidth={14} style={{ cursor: 'pointer' }} />

      <EdgeLabelRenderer>
        {data?.diag && (
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              // Above the label point so a status pill / binding chip keeps its own spot.
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY - 16}px)`,
              pointerEvents: 'all',
              zIndex: 2,
            }}
            title={
              diagTitle ??
              `${diagCount} ${data.diag} diagnostic(s)${diagStale ? ' — from last validation' : ''}`
            }
            role="img"
            aria-label={`${diagCount} ${data.diag} finding${diagCount > 1 ? 's' : ''} on this edge${diagStale ? ' (from last validation)' : ''}`}
          >
            <span
              className={`ol-edge-diagchip ol-edge-diagchip--${data.diag}${diagStale ? ' ol-edge-diagchip--stale' : ''}`}
            >
              {diagSymbol}
              {diagCount > 1 ? diagCount : ''}
            </span>
          </div>
        )}
        {kind === 'status' && data?.onStatus && (
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
            }}
          >
            <StatusPill status={data.onStatus} />
          </div>
        )}
        {isData && data?.binding && (
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              opacity: selected ? 1 : 0.85,
            }}
            title={`data binding: ${data.binding.source} → ${data.binding.target} (never gates)`}
          >
            <span className="ol-edge-datachip">
              {data.binding.source} → {data.binding.target}
            </span>
          </div>
        )}
        {kind === 'sequence' && data?.guard && (
          <div
            className="nodrag nopan"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
            }}
            title={`guard: ${data.guard}`}
          >
            <span className="ol-edge-pill" style={{ color: 'var(--ol-text-secondary)' }}>
              ƒ guard
            </span>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
}
